import { useState, useEffect } from 'react';
import { Search, Filter, Grid, List } from 'lucide-react';
import { useCars } from '../context/CarContext';
import CarCard from '../components/CarCard';
import { Car } from '../types';
import { updatePageTitle, updateMetaDescription, updateCanonicalUrl } from '../utils/seoUtils';
import { BUSINESS_CONFIG } from '../config/business';

const Home = () => {
  const { getAvailableCars, searchCars } = useCars();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCars, setFilteredCars] = useState<Car[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  const availableCars = getAvailableCars();

  // SEO optimization for home page
  useEffect(() => {
    const homeTitle = `Quality Used Cars in Florida`;
    const homeDescription = `Browse our extensive inventory of quality used cars in Florida at ${BUSINESS_CONFIG.businessName}. Find reliable pre-owned vehicles with competitive prices and excellent customer service. Cars, trucks, SUVs available now.`;
    const currentUrl = window.location.origin;
    
    updatePageTitle(homeTitle);
    updateMetaDescription(homeDescription);
    updateCanonicalUrl(currentUrl);
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      // Search in available and pending cars (exclude sold)
      const allSearchResults = searchCars(searchQuery);
      const publicSearchResults = allSearchResults.filter(car => 
        car.saleStatus === 'available' || car.saleStatus === 'pending'
      );
      setFilteredCars(publicSearchResults);
    } else {
      setFilteredCars(availableCars);
    }
  }, [searchQuery, availableCars, searchCars]);

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center bg-no-repeat text-white rounded-xl p-8 text-center min-h-[400px] flex flex-col justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url('https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">Find Your Perfect Car</h1>
          <p className="text-xl md:text-2xl text-gray-100 mb-6 drop-shadow-md">
            Quality Used Cars in Florida - Competitive Prices & Excellent Service
          </p>
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by make, model, color..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors text-gray-300"
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
          </button>
          <span className="text-gray-400">
            {filteredCars.length} {filteredCars.length === 1 ? 'vehículo' : 'vehículos'} encontrados
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid' 
                ? 'bg-blue-900/50 text-blue-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Grid className="h-5 w-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list' 
                ? 'bg-blue-900/50 text-blue-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 animate-slide-up">
          <h3 className="font-semibold text-lg mb-4 text-gray-100">Filtros de Búsqueda</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Make Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Marca</label>
              <select className="input-field">
                <option value="">Todas las marcas</option>
                {Array.from(new Set(availableCars.map((car: Car) => car.make))).map((make: string) => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Precio Máximo</label>
              <select className="input-field">
                <option value="">Sin límite</option>
                <option value="20000">Hasta $20,000</option>
                <option value="30000">Hasta $30,000</option>
                <option value="50000">Hasta $50,000</option>
                <option value="100000">Hasta $100,000</option>
              </select>
            </div>

            {/* Fuel Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Combustible</label>
              <select className="input-field">
                <option value="">Todos</option>
                <option value="Gasoline">Gasolina</option>
                <option value="Diesel">Diésel</option>
                <option value="Electric">Eléctrico</option>
                <option value="Hybrid">Híbrido</option>
              </select>
            </div>

            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Condición</label>
              <select className="input-field">
                <option value="">Todas</option>
                <option value="New">Nuevo</option>
                <option value="Used">Usado</option>
                <option value="Certified Pre-Owned">Certificado</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Cars Grid/List */}
      {filteredCars.length > 0 ? (
        <div className={`${
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
            : 'space-y-4'
        }`}>
          {filteredCars.map(car => (
            <CarCard key={car.id} car={car} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron vehículos</h3>
          <p className="text-gray-600 mb-4">
            Intenta ajustar tu búsqueda o filtros para encontrar más opciones.
          </p>
          
          {/* Debug info */}
          <div className="text-sm text-gray-500 mt-4">
            <p>Total de carros disponibles: {availableCars.length}</p>
            <p>Carros filtrados: {filteredCars.length}</p>
            <p className="mt-2 text-blue-600">
              Use el Panel de Administración para agregar nuevos carros.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;